import { useCallback, useContext, useMemo } from "react";
// analytics
import {
    clickBullsEyeTgAddCta,
    clickBullsEyeTgSkipCta,
    impressionBullsEyeTgAddCta,
    impressionBullsEyeTgHalfcardMount,
    impressionBullsEyeTgSkipCta,
    clickViewAllBenefitsCta,
    impressionBullsEyeTgHalfcardGratificationMount,
} from "@cart-client/analytics/events";
// context
import { AnalyticsContext, DEFAULT_FREQUENCY_CAP_VALUE } from "@swiggy-private/checkout-core";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";
// types
import { M2CCardComms, UserSuperStatus, M2cTypes } from "@swiggy-private/cart-types";
import { PlanDetails } from "@swiggy-private/cart-types";
// utils
import { LocalStorage } from "@swiggy-private/core-ui";

export const useM2CHalfcardAnalytics = (
    userSuperStatus: UserSuperStatus | string,
    planDetails: PlanDetails,
    m2cCardComms: M2CCardComms | undefined,
) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);
    const { showM2ChalfcardOnClick } = useContext(UpdateCartContext);

    const extraM2cData = useMemo(() => {
        if (m2cCardComms?.identifiers?.m2c_type === M2cTypes.M2C_BLACK_RENEWAL) {
            const { asset_ctx: assetCtx, content, logo_ctx: logoCtx } = m2cCardComms;
            return { logo: logoCtx.logo, asset: assetCtx, content };
        }
        return {};
    }, [m2cCardComms]);

    const buildContext = useCallback(
        (payload?: Record<string, string>) =>
            JSON.stringify({
                userSuperStatus,
                ...planDetails,
                ...payload,
                ...extraM2cData,
                sourceWidget: showM2ChalfcardOnClick?.flowType,
            }),
        [extraM2cData, planDetails, showM2ChalfcardOnClick?.flowType, userSuperStatus],
    );

    const commonContext = useMemo(() => buildContext(), [buildContext]);

    const onImpressionM2CHalfcard = useCallback(
        (title: string, key: string) => {
            const frequencyData = LocalStorage.getItem(key, DEFAULT_FREQUENCY_CAP_VALUE);
            return impressionBullsEyeTgHalfcardMount(
                impressionEvent,
                buildContext({ title, frequencyCapCount: String(frequencyData?.counterValue) }),
            );
        },
        [buildContext, impressionEvent],
    );

    const onImpressionM2CHalfcardGratification = useCallback(
        (title?: string, content?: string) => {
            return impressionBullsEyeTgHalfcardGratificationMount(
                impressionEvent,
                buildContext({ title: title || "", content: content || "" }),
            );
        },
        [buildContext, impressionEvent],
    );

    const onImpressionM2CHalfcardAddCta = useCallback(
        (ctaText: string) => {
            return impressionBullsEyeTgAddCta(impressionEvent, ctaText, commonContext);
        },
        [commonContext, impressionEvent],
    );

    const onImpressionM2CHalfcardSkipCta = useCallback(
        (ctaText: string) => {
            return impressionBullsEyeTgSkipCta(impressionEvent, ctaText, commonContext);
        },
        [commonContext, impressionEvent],
    );

    const onClickM2CHalfcardAddCta = useCallback(
        (ctaText: string) => {
            clickBullsEyeTgAddCta(clickEvent, commonContext, ctaText);
        },
        [clickEvent, commonContext],
    );

    const onClickM2CHalfcardSkipCta = useCallback(
        (ctaText: string, title?: string, content?: string) => {
            clickBullsEyeTgSkipCta(clickEvent, buildContext({ title: title || "", content: content || "" }), ctaText);
        },
        [buildContext, clickEvent],
    );

    const onClickM2CHalfcardViewAllBenefitsCta = useCallback(
        (ctaText: string, ctaLink: string) => {
            clickViewAllBenefitsCta(clickEvent, buildContext({ ctaText, ctaLink }));
        },
        [buildContext, clickEvent],
    );

    return {
        onImpressionM2CHalfcard,
        onImpressionM2CHalfcardAddCta,
        onImpressionM2CHalfcardSkipCta,
        onClickM2CHalfcardAddCta,
        onClickM2CHalfcardSkipCta,
        onClickM2CHalfcardViewAllBenefitsCta,
        onImpressionM2CHalfcardGratification,
    };
};
